/**
 * Meta Pixel Sprint 2026-08 — versioned consent store (Web).
 *
 * DealAlert has zero third-party marketing scripts wired today. This
 * store gates the Meta Pixel behind an explicit, versioned opt-in.
 * A future analytics category (currently unused) can plug in by
 * adding a new key to CATEGORIES without breaking existing consents.
 *
 * Storage:
 *   ``localStorage['dealalert_consent_v1']`` = JSON string
 *   ``{ version:1, categories:{ necessary:true, marketing:bool },
 *       decidedAt:iso }``
 *
 * "No decision yet" is signalled by the ABSENCE of this key. The
 * banner is shown in that case; ``getMarketingConsent()`` returns
 * false so no tracking runs before the user chooses.
 *
 * A version bump (e.g. adding a new category) invalidates every old
 * consent — the user is re-prompted. This is the DSGVO-compliant
 * default: a broadened scope requires a fresh opt-in.
 */

const STORAGE_KEY = "dealalert_consent_v1";
const CURRENT_VERSION = 1;

/** All known categories. `necessary` is always true; the user cannot
 *  opt out of first-party session cookies / CSRF etc. */
export const CATEGORIES = /** @type {const} */ (["necessary", "marketing"]);

const listeners = new Set();

function _emit() {
  for (const fn of listeners) {
    try {
      fn(readConsent());
    } catch {
      /* one broken listener must not stop the others */
    }
  }
}

function _readRaw() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== CURRENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Public read — returns the persisted document or a "no decision"
 *  shape. `decided: false` means the banner should be shown. */
export function readConsent() {
  const raw = _readRaw();
  if (!raw) {
    return {
      decided: false,
      version: CURRENT_VERSION,
      categories: { necessary: true, marketing: false },
      decidedAt: null,
    };
  }
  return {
    decided: true,
    version: raw.version,
    categories: {
      necessary: true,
      marketing: Boolean(raw.categories?.marketing),
    },
    decidedAt: raw.decidedAt || null,
  };
}

/** Convenience — the only category the Meta Pixel wrapper cares about. */
export function getMarketingConsent() {
  const c = readConsent();
  return c.decided && c.categories.marketing === true;
}

function _writeDecision(categories) {
  const doc = {
    version: CURRENT_VERSION,
    categories: {
      necessary: true,
      marketing: Boolean(categories.marketing),
    },
    decidedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(doc));
  } catch {
    /* private-mode / quota — decision is session-only */
  }
  _emit();
  return readConsent();
}

/** Convenience helpers exposed to the banner. */
export const acceptAll = () => _writeDecision({ marketing: true });
export const rejectAll = () => _writeDecision({ marketing: false });
export const savePartial = (categories) => _writeDecision(categories);

/** Wipe the decision entirely — used by the "reset for testing"
 *  hook and by a user who explicitly asks for it on the settings
 *  page. The banner will re-appear on next render. */
export function clearConsent() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
  _emit();
}

/** Subscribe to any change — returns an unsubscribe function. Used
 *  by the Meta Pixel wrapper to react to a revocation immediately. */
export function subscribe(fn) {
  if (typeof fn !== "function") return () => {};
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Testing helper — resets internal state. */
export function _resetConsentStoreForTests() {
  listeners.clear();
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}
